// Central definitions for status enums.
//
// Each enum is declared once here as:
//   - a `*_STATUSES` tuple   → feeds `z.enum(...)` in forms and the DB schema's type
//   - a `*_STATUS_OPTIONS`    → feeds `EnumSelect` (value + human label)
//
// Keeping both in one place means a new status is added in exactly one spot and
// every form, filter, and create dialog picks it up.

export interface EnumOption {
  value: string;
  label: string;
}

// --- WIDGET_FEATURE_START ---
// ── Widget (the reference entity — see src/db/schema/widget.ts) ──
//
// `widget` is the template's one example entity. When you add a real entity,
// declare its statuses here the same way and import them in its form/filter.
// This whole section is a WIDGET_FEATURE block, so `setup.ts` strips it along
// with src/examples/widget when the reference entity is disabled — keep the
// markers OUTSIDE these comments so no orphan prose is left behind.
export const WIDGET_STATUSES = ["draft", "active", "archived"] as const;
export type WidgetStatus = (typeof WIDGET_STATUSES)[number];
export const WIDGET_STATUS_OPTIONS: readonly EnumOption[] = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];
// --- WIDGET_FEATURE_END ---

// ── Audit log ──
//
// `action` is the `<entity>.<verb>` string stored on every audit_log row, and
// `entityType` is the coarse kind it groups under. Both are validated against
// these allowlists in `recordAudit` (src/lib/audit.ts) and drive the filter
// dropdowns in the admin viewer. Add a new action here when you audit a new
// mutation — the helper rejects anything not listed (see .claude/rules/audit.md).

export const AUDIT_ENTITY_TYPES = [
  // --- WIDGET_FEATURE_START ---
  // These marker comments double as the reason this array stays multi-line:
  // the formatter would otherwise collapse it to one line, and a one-line
  // array can't carry a strippable block. Stripped with the WIDGET feature,
  // after which the formatter is free to collapse what's left.
  "widget",
  // --- WIDGET_FEATURE_END ---
  "user",
  "auth",
] as const;
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number];

export const AUDIT_ACTIONS = [
  // --- WIDGET_FEATURE_START ---
  // Reference-entity CRUD (src/examples/widget). Mirror this block per entity
  // you audit: `<entity>.created` / `.updated` / `.deleted`.
  "widget.created",
  "widget.updated",
  "widget.deleted",
  // --- WIDGET_FEATURE_END ---
  // Authentication events (captured via Better Auth hooks).
  "auth.login",
  "auth.login_failed",
  "auth.logout",
  "auth.signup",
  "auth.password_reset_requested",
  "auth.password_reset_completed",
  // Admin user management (Better Auth admin plugin endpoints).
  "user.created",
  "user.deleted",
  "user.role_changed",
  "user.banned",
  "user.unbanned",
  "user.password_reset",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];
export const AUDIT_ACTION_OPTIONS: readonly EnumOption[] = AUDIT_ACTIONS.map((value) => ({
  value,
  label: value,
}));
